import React, { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { useDeepMemo } from '@beeline.com/bp-design-system';
import { useQuery } from '@apollo/client';
import { PageModelInput } from '@utmost.co/enterprise-models';
import { FETCH_POLICY } from 'common/apollo/FETCH_POLICY';
import { sleep } from 'src/utils/sleep';
import { GET_TASK_NOTIFICATIONS } from './TaskNotifications.queries';
import { PartialTaskNotifications, TasksContextProps } from './TaskNotifications.types';

export const TasksNotificationsContext = createContext<TasksContextProps>({
  refetchTasks: () => {},
  handleTasksWSUpdate: () => {},
  tasks: [],
  loading: false,
  error: undefined,
  tasksToReviewCount: 0
});

interface Props {
  userUid: string | undefined;
}

const MAX_RETRIES = 3;

export const TaskNotificationsProvider: FC<React.PropsWithChildren<Props>> = ({ children, userUid }) => {
  const { data, loading, error, refetch } = useQuery<
    { tasks: { totalCount: number; tasks: PartialTaskNotifications[] } },
    { assigneeOnly: boolean; page?: PageModelInput }
  >(GET_TASK_NOTIFICATIONS, {
    variables: { assigneeOnly: true, page: { offset: 0, limit: 5 } },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: FETCH_POLICY.NO_CACHE,
    skip: !userUid
  });

  const tasks = useMemo(() => data?.tasks?.tasks ?? [], [data?.tasks?.tasks]);
  const tasksToReviewCount = data?.tasks.totalCount || 0;

  const refetchTasksHandler = useCallback(async () => {
    const previousTaskCount = tasks.length;
    let newTasks = await refetch();
    let retryCount = 0;
    // The task count may not change immediately after a task is completed,
    // so retry to refetch the tasks until the task count changes.
    while (retryCount < MAX_RETRIES && newTasks.data?.tasks?.tasks?.length === previousTaskCount) {
      await sleep();
      retryCount++;
      newTasks = await refetch();
    }
  }, [refetch, tasks.length]);

  const value: TasksContextProps = useDeepMemo(
    (): TasksContextProps => ({
      refetchTasks: refetchTasksHandler,
      handleTasksWSUpdate: refetchTasksHandler,
      tasks,
      loading,
      error,
      tasksToReviewCount
    }),
    [refetchTasksHandler, tasks, loading, error, tasksToReviewCount]
  );

  return <TasksNotificationsContext.Provider value={value}>{children}</TasksNotificationsContext.Provider>;
};

export const useTasksContext = () => useContext<TasksContextProps>(TasksNotificationsContext);
